import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '@/lib/operator-api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Database,
  Server,
  Cloud,
  Wifi,
  HardDrive,
  Shield,
  Zap,
  Globe,
  RefreshCw,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  MessageSquare,
  CreditCard,
  Mail,
  FileText,
  Lock,
  Settings,
  Link2,
  type LucideIcon,
} from 'lucide-react';

interface ServiceStatus {
  name: string;
  url?: string;
  description?: string;
  status: 'healthy' | 'unhealthy' | 'unknown';
  responseTime?: number;
  message?: string;
}

interface HealthResponse {
  status: string;
  timestamp: string;
  services: ServiceStatus[];
}

interface IntegrationStatus {
  name: string;
  provider: string;
  description: string;
  status: 'connected' | 'disconnected' | 'not_configured' | 'degraded';
  configured: boolean;
  responseTime?: number;
  details?: Record<string, unknown>;
}

interface IntegrationsResponse {
  success: boolean;
  timestamp: string;
  integrations: IntegrationStatus[];
  summary: {
    total: number;
    connected: number;
    disconnected: number;
    notConfigured: number;
  };
}

const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

// Icon mapping for service types
const getServiceIcon = (serviceName: string): LucideIcon => {
  const name = serviceName.toLowerCase();
  if (name.includes('database') || name.includes('postgres') || name.includes('mysql') || name.includes('mongo') || name.includes('redis')) {
    return Database;
  }
  if (name.includes('api') || name.includes('server') || name.includes('backend')) {
    return Server;
  }
  if (name.includes('cloud') || name.includes('aws') || name.includes('azure') || name.includes('gcp')) {
    return Cloud;
  }
  if (name.includes('network') || name.includes('cdn') || name.includes('dns')) {
    return Wifi;
  }
  if (name.includes('storage') || name.includes('s3') || name.includes('blob')) {
    return HardDrive;
  }
  if (name.includes('auth') || name.includes('security') || name.includes('oauth')) {
    return Shield;
  }
  if (name.includes('cache') || name.includes('memory')) {
    return Zap;
  }
  return Globe;
};

// Icon mapping for integration providers
const getIntegrationIcon = (provider: string, name: string): LucideIcon => {
  const p = provider.toLowerCase();
  const n = name.toLowerCase();

  if (p.includes('stripe') || p.includes('payment') || n.includes('payment')) {
    return CreditCard;
  }
  if (p.includes('twilio') || p.includes('whatsapp') || n.includes('messaging') || n.includes('sms')) {
    return MessageSquare;
  }
  if (p.includes('sendgrid') || p.includes('mailgun') || p.includes('email') || n.includes('email')) {
    return Mail;
  }
  if (p.includes('openai') || p.includes('anthropic') || p.includes('ai') || n.includes('ai')) {
    return Zap;
  }
  if (p.includes('auth') || n.includes('auth') || p.includes('oauth')) {
    return Lock;
  }
  if (p.includes('storage') || p.includes('s3') || n.includes('storage')) {
    return HardDrive;
  }
  if (p.includes('analytics') || n.includes('analytics')) {
    return Activity;
  }
  if (p.includes('document') || n.includes('document')) {
    return FileText;
  }
  return Link2;
};

export default function TechnicalTab() {
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [integrations, setIntegrations] = useState<IntegrationStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingIntegrations, setIsLoadingIntegrations] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [integrationsError, setIntegrationsError] = useState<string | null>(null);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchHealthStatus = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/api/health/services', { signal });

      // 503 = degraded but response body still has valid per-service data
      if (!response.ok && response.status !== 503) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: HealthResponse = await response.json();
      if (signal?.aborted) return;
      setServices(data.services || []);
      setLastChecked(new Date());
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch health status';
      setError(errorMessage);
      setServices([]);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, []);

  const fetchIntegrationsStatus = useCallback(async (signal?: AbortSignal) => {
    setIsLoadingIntegrations(true);
    setIntegrationsError(null);

    try {
      const response = await apiFetch('/api/health/integrations', { signal });

      // 503 = some integrations unhealthy, but response body has per-integration data
      if (!response.ok && response.status !== 503) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: IntegrationsResponse = await response.json();
      if (signal?.aborted) return;
      setIntegrations(data.integrations || []);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch integrations status';
      setIntegrationsError(errorMessage);
      setIntegrations([]);
    } finally {
      if (!signal?.aborted) setIsLoadingIntegrations(false);
    }
  }, []);

  const fetchAll = useCallback(async (signal?: AbortSignal) => {
    await Promise.all([fetchHealthStatus(signal), fetchIntegrationsStatus(signal)]);
  }, [fetchHealthStatus, fetchIntegrationsStatus]);

  // Initial fetch on mount
  useEffect(() => {
    const controller = new AbortController();
    fetchAll(controller.signal);
    return () => controller.abort();
  }, [fetchAll]);

  // Auto-refresh effect
  useEffect(() => {
    const controller = new AbortController();
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchAll(controller.signal), AUTO_REFRESH_INTERVAL);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      controller.abort();
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, fetchAll]);

  const healthyCount = services.filter((s) => s.status === 'healthy').length;
  const totalCount = services.length;

  const connectedCount = integrations.filter((i) => i.status === 'connected').length;
  const integrationsTotalCount = integrations.length;

  const getIntegrationStatusIndicator = (status: IntegrationStatus['status']) => {
    switch (status) {
      case 'connected':
        return (
          <span className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10B981] opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#10B981]" />
            </span>
            <span className="text-[#059669] font-medium">Connected</span>
          </span>
        );
      case 'disconnected':
        return (
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ea580c]" />
            <span className="text-[#ea580c] font-medium">Disconnected</span>
          </span>
        );
      case 'degraded':
        return (
          <span className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-500 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
            </span>
            <span className="text-amber-600 font-medium">Degraded</span>
          </span>
        );
      case 'not_configured':
      default:
        return (
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#756856]" />
            <span className="text-[#756856]">Not Configured</span>
          </span>
        );
    }
  };

  const getStatusIndicator = (status: ServiceStatus['status']) => {
    switch (status) {
      case 'healthy':
        return (
          <span className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10B981] opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#10B981]" />
            </span>
            <span className="text-[#059669] font-medium">Healthy</span>
          </span>
        );
      case 'unhealthy':
        return (
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ea580c]" />
            <span className="text-[#ea580c] font-medium">Unhealthy</span>
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#756856]" />
            <span className="text-[#756856]">Unknown</span>
          </span>
        );
    }
  };

  const formatResponseTime = (ms?: number) => {
    if (ms === undefined || ms === null) return '-';
    const color = ms < 100 ? 'text-[#059669]' : ms < 300 ? 'text-amber-600' : 'text-[#ea580c]';
    return <span className={color}>{ms}ms</span>;
  };

  const formatLastChecked = (date: Date | null) => {
    if (!date) return 'Never';
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <Card className="border-[#f3efe9] shadow-sm overflow-hidden">
        <CardHeader className="pb-3 bg-gradient-to-r from-[#f9f7f4] to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0D4F3C]/10">
                <Activity className="h-5 w-5 text-[#0D4F3C]" aria-hidden="true" />
              </div>
              <CardTitle className="text-lg font-semibold text-[#1f1c18]">Services Health Status</CardTitle>
            </div>
            <div className="flex items-center gap-4">
              {/* Auto-refresh Toggle */}
              <label className="flex items-center gap-3 cursor-pointer group">
                <span className="text-sm text-[#756856] group-hover:text-[#1f1c18] transition-colors">
                  Auto-refresh
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={autoRefresh}
                  aria-label="Toggle auto-refresh"
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#0D4F3C] focus:ring-offset-2 ${
                    autoRefresh
                      ? 'bg-[#0D4F3C] shadow-inner'
                      : 'bg-[#f3efe9] hover:bg-[#e8e4dd]'
                  }`}
                >
                  <span
                    className={`inline-flex h-4 w-4 transform items-center justify-center rounded-full bg-white shadow-md transition-all duration-300 ${
                      autoRefresh ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  >
                    {autoRefresh && (
                      <RefreshCw className="h-2.5 w-2.5 text-[#0D4F3C] animate-spin" style={{ animationDuration: '3s' }} />
                    )}
                  </span>
                </button>
              </label>

              {/* Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAll()}
                disabled={isLoading || isLoadingIntegrations}
                className="border-[#0D4F3C]/20 hover:border-[#0D4F3C] hover:bg-[#0D4F3C]/5 text-[#0D4F3C] transition-all duration-200 disabled:opacity-50"
              >
                {isLoading || isLoadingIntegrations ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Checking...</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4" />
                    <span>Refresh</span>
                  </span>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            {/* Summary Badge */}
            <div className="flex items-center gap-3">
              {totalCount > 0 ? (
                <>
                  <Badge
                    variant={healthyCount === totalCount ? 'completed' : healthyCount > 0 ? 'pending' : 'destructive'}
                    className="text-sm px-3 py-1.5 font-medium"
                  >
                    {healthyCount === totalCount ? (
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                    ) : healthyCount === 0 ? (
                      <XCircle className="h-3.5 w-3.5 mr-1.5" />
                    ) : (
                      <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {healthyCount} of {totalCount} services healthy
                  </Badge>
                  {healthyCount === totalCount && (
                    <span className="text-sm text-[#059669] font-medium flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" />
                      All systems operational
                    </span>
                  )}
                  {healthyCount > 0 && healthyCount < totalCount && (
                    <span className="text-sm text-amber-600 font-medium flex items-center gap-1.5">
                      <AlertTriangle className="h-4 w-4" />
                      Some services degraded
                    </span>
                  )}
                  {healthyCount === 0 && totalCount > 0 && (
                    <span className="text-sm text-[#ea580c] font-medium flex items-center gap-1.5">
                      <XCircle className="h-4 w-4" />
                      System outage
                    </span>
                  )}
                </>
              ) : (
                <span className="text-sm text-[#756856] flex items-center gap-1.5">
                  <Settings className="h-4 w-4" />
                  No services configured
                </span>
              )}
            </div>

            {/* Last Checked */}
            <div className="text-sm text-[#756856] flex items-center gap-1.5">
              <Clock className="h-4 w-4" />
              Last checked: {formatLastChecked(lastChecked)}
              {autoRefresh && (
                <Badge variant="outline" className="ml-2 text-xs bg-[#0D4F3C]/5 border-[#0D4F3C]/20 text-[#0D4F3C]">
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" style={{ animationDuration: '3s' }} />
                  Auto
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Card className="border-[#ea580c]/30 bg-gradient-to-r from-[#ea580c]/5 to-[#ea580c]/10 shadow-sm">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-[#ea580c]/10 shrink-0">
                <AlertTriangle className="h-5 w-5 text-[#ea580c]" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-[#ea580c] mb-1">Connection Error</h4>
                <p className="text-sm text-[#ea580c]/80">{error}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchAll()}
                className="text-[#ea580c] hover:text-[#ea580c] hover:bg-[#ea580c]/10"
              >
                <RefreshCw className="h-4 w-4 mr-1.5" />
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Services Table */}
      <Card className="border-[#f3efe9] shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f9f7f4] hover:bg-[#f9f7f4] border-b border-[#f3efe9]">
                <TableHead className="w-[180px] text-[#1f1c18] font-semibold">Service</TableHead>
                <TableHead className="w-[200px] text-[#1f1c18] font-semibold">Connection</TableHead>
                <TableHead className="text-[#1f1c18] font-semibold">Description</TableHead>
                <TableHead className="w-[140px] text-[#1f1c18] font-semibold">Status</TableHead>
                <TableHead className="w-[100px] text-right text-[#1f1c18] font-semibold">Response</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="relative">
                        <div className="h-10 w-10 rounded-full border-2 border-[#0D4F3C]/20 border-t-[#0D4F3C] animate-spin" />
                        <Activity className="h-5 w-5 text-[#0D4F3C] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                      <span className="text-[#756856] font-medium">Loading services...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="p-3 rounded-full bg-[#f3efe9]">
                        <Server className="h-6 w-6 text-[#756856]" />
                      </div>
                      <div>
                        <p className="text-[#1f1c18] font-medium">No services found</p>
                        <p className="text-sm text-[#756856]">Check your health endpoint configuration.</p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                services.map((service, index) => {
                  const ServiceIcon = getServiceIcon(service.name);
                  return (
                    <TableRow
                      key={`${service.name}-${index}`}
                      className={`
                        transition-colors duration-150
                        ${index % 2 === 0 ? 'bg-white' : 'bg-[#fdfcfb]'}
                        hover:bg-[#f9f7f4]
                        border-b border-[#f3efe9] last:border-b-0
                      `}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            service.status === 'healthy'
                              ? 'bg-[#10B981]/10 text-[#059669]'
                              : service.status === 'unhealthy'
                                ? 'bg-[#ea580c]/10 text-[#ea580c]'
                                : 'bg-[#f3efe9] text-[#756856]'
                          }`}>
                            <ServiceIcon className="h-4 w-4" aria-hidden="true" />
                          </div>
                          <span className="text-[#1f1c18]">{service.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="text-xs bg-[#f3efe9] px-2 py-1 rounded-md font-mono text-[#756856] border border-[#e8e4dd]">
                          {service.url || 'internal'}
                        </code>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-[#756856]">
                          {service.description || 'No description available'}
                        </span>
                      </TableCell>
                      <TableCell>{getStatusIndicator(service.status)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatResponseTime(service.responseTime)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* External Integrations Section */}
      <Card className="mt-8 border-[#f3efe9] shadow-sm overflow-hidden">
        <CardHeader className="pb-3 bg-gradient-to-r from-[#f9f7f4] to-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#0D4F3C]/10">
                <Link2 className="h-5 w-5 text-[#0D4F3C]" aria-hidden="true" />
              </div>
              <CardTitle className="text-lg font-semibold text-[#1f1c18]">External Integrations</CardTitle>
            </div>
            {integrationsTotalCount > 0 && (
              <Badge
                variant={connectedCount === integrationsTotalCount ? 'completed' : connectedCount > 0 ? 'pending' : 'outline'}
                className="text-sm px-3 py-1.5 font-medium"
              >
                {connectedCount === integrationsTotalCount ? (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                ) : connectedCount > 0 ? (
                  <AlertTriangle className="h-3.5 w-3.5 mr-1.5" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                )}
                {connectedCount} of {integrationsTotalCount} connected
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {integrationsError && (
            <div className="px-4 py-3 bg-gradient-to-r from-[#ea580c]/5 to-[#ea580c]/10 border-b border-[#ea580c]/20">
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-full bg-[#ea580c]/10">
                  <AlertTriangle className="h-4 w-4 text-[#ea580c]" aria-hidden="true" />
                </div>
                <span className="text-sm text-[#ea580c] font-medium">{integrationsError}</span>
              </div>
            </div>
          )}
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f9f7f4] hover:bg-[#f9f7f4] border-b border-[#f3efe9]">
                <TableHead className="w-[200px] text-[#1f1c18] font-semibold">Integration</TableHead>
                <TableHead className="text-[#1f1c18] font-semibold">Description</TableHead>
                <TableHead className="w-[150px] text-[#1f1c18] font-semibold">Status</TableHead>
                <TableHead className="w-[100px] text-right text-[#1f1c18] font-semibold">Response</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingIntegrations && integrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="relative">
                        <div className="h-10 w-10 rounded-full border-2 border-[#0D4F3C]/20 border-t-[#0D4F3C] animate-spin" />
                        <Link2 className="h-5 w-5 text-[#0D4F3C] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                      </div>
                      <span className="text-[#756856] font-medium">Loading integrations...</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : integrations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-3">
                      <div className="p-3 rounded-full bg-[#f3efe9]">
                        <Link2 className="h-6 w-6 text-[#756856]" />
                      </div>
                      <div>
                        <p className="text-[#1f1c18] font-medium">No integrations found</p>
                        <p className="text-sm text-[#756856]">Configure external services to see them here.</p>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                integrations.map((integration, index) => {
                  const IntegrationIcon = getIntegrationIcon(integration.provider, integration.name);
                  return (
                    <TableRow
                      key={`${integration.name}-${index}`}
                      className={`
                        transition-colors duration-150
                        ${index % 2 === 0 ? 'bg-white' : 'bg-[#fdfcfb]'}
                        hover:bg-[#f9f7f4]
                        border-b border-[#f3efe9] last:border-b-0
                      `}
                    >
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${
                            integration.status === 'connected'
                              ? 'bg-[#10B981]/10 text-[#059669]'
                              : integration.status === 'disconnected'
                                ? 'bg-[#ea580c]/10 text-[#ea580c]'
                                : integration.status === 'degraded'
                                  ? 'bg-amber-500/10 text-amber-600'
                                  : 'bg-[#f3efe9] text-[#756856]'
                          }`}>
                            <IntegrationIcon className="h-4 w-4" aria-hidden="true" />
                          </div>
                          <div>
                            <span className="text-[#1f1c18] block">{integration.name}</span>
                            <span className="text-xs text-[#756856]">{integration.provider}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-[#756856]">
                          {integration.description}
                        </span>
                      </TableCell>
                      <TableCell>{getIntegrationStatusIndicator(integration.status)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {integration.responseTime ? formatResponseTime(integration.responseTime) : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="text-center mt-6">
        <p className="text-xs text-[#756856] inline-flex items-center gap-1.5">
          <Activity className="h-3.5 w-3.5" />
          Services and integrations are checked via health endpoints.
          {autoRefresh && (
            <span className="inline-flex items-center gap-1 ml-1 text-[#0D4F3C]">
              <RefreshCw className="h-3 w-3 animate-spin" style={{ animationDuration: '3s' }} />
              Auto-refresh active (every 30 seconds)
            </span>
          )}
        </p>
      </div>
    </div>
  );
}
